import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

/** All attachments of an approval record, flattened from its step logs. */
export function collectApprovalAttachments(item) {
  return (item?.logs || []).flatMap((log) =>
    (log?.attachments || []).map((attachment) => ({
      ...attachment,
      stepLabel: log.stepLabel || log.stepKey || "",
      resolvedBy: log.resolvedBy?.name || "",
      resolvedAt: log.resolvedAt || null,
    })),
  );
}

export function isImageAttachment(attachment) {
  return String(attachment?.mimeType || "").startsWith("image/");
}

export function isPdfAttachment(attachment) {
  return String(attachment?.mimeType || "") === "application/pdf";
}

export function isPreviewableAttachment(attachment) {
  return isImageAttachment(attachment) || isPdfAttachment(attachment);
}

export async function fetchApprovalAttachmentBlob(attachment, token) {
  const response = await axios.get(`${API_URL}${attachment.downloadUrl}`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: "blob",
  });
  const mimeType = attachment.mimeType || response.data.type || "application/octet-stream";
  return new Blob([response.data], { type: mimeType });
}

export async function createApprovalAttachmentObjectUrl(attachment, token) {
  const blob = await fetchApprovalAttachmentBlob(attachment, token);
  return window.URL.createObjectURL(blob);
}

export async function downloadApprovalAttachment(attachment, token) {
  const blob = await fetchApprovalAttachmentBlob(attachment, token);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = attachment.originalName;
  link.click();
  window.URL.revokeObjectURL(url);
}

/** Open system share for the file. */
export async function shareApprovalAttachment(attachment, token) {
  const blob = await fetchApprovalAttachmentBlob(attachment, token);
  const name = attachment.originalName || "attachment";
  const mimeType = attachment.mimeType || blob.type || "application/octet-stream";
  const file = new File([blob], name, { type: mimeType });

  if (
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    (!navigator.canShare || navigator.canShare({ files: [file] }))
  ) {
    try {
      await navigator.share({
        files: [file],
        title: name,
        text: name,
      });
      return;
    } catch (err) {
      if (err?.name === "AbortError") return;
      throw err;
    }
  }

  // Desktop browsers without Web Share: open a download as last-resort usability
  await downloadApprovalAttachment(attachment, token);
}
